import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  Row,
  Col,
  Card,
  CardBody,
  Alert,
  UncontrolledAccordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Spinner,
  Button,
  Badge,
  Table,
} from "reactstrap";
import { del, get, post } from "../../helpers/api_helper";
import { DIRECT_DELIVERY_LOGS, DIRECT_DELIVERY_RUN, REMOVE_DIRECT_DELIVERY_LOG } from "helpers/api_endpoints";

document.title = "Direct Delivery Logs";

const DirectDeliveryLogs = () => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [directDeliveryLogs, setDirectDeliveryLogs] = useState([]);

  const fetchDirectDeliveryLogs = async () => {
    setErrorMsg("");
    setLoading(true);
    try {
      const response = await get(DIRECT_DELIVERY_LOGS);
      setDirectDeliveryLogs(response.data);
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to fetch direct delivery logs");
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  const removeDirectDeliveryLog = async (id) => {
    setErrorMsg("");
    setLoading(true);
    try {
      await del(REMOVE_DIRECT_DELIVERY_LOG, { id });
      fetchDirectDeliveryLogs();
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to remove direct delivery log");
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  const runDirectDeliveryAlgorithm = async () => {
    setErrorMsg("");
    setLoading(true);
    try {
      await post(DIRECT_DELIVERY_RUN);
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to run direct delivery algorithm");
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setLoading(false);
      fetchDirectDeliveryLogs();
    }
  };

  useEffect(() => {
    fetchDirectDeliveryLogs();
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      }
    } catch (e) {
      console.warn('Failed to parse timestamp:', timestamp, e);
    }
    
    return 'Invalid Date';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    } catch (e) {
      console.warn('Failed to parse timestamp:', timestamp, e);
    }
    
    return 'Invalid Date';
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Direct Delivery" breadcrumbItem="Direct Delivery Logs" />
          
          <Row className="mb-3">
            <Col md={6}>
              <Button color="primary" onClick={fetchDirectDeliveryLogs} className="me-2" disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Refresh Logs'}
              </Button>
              <Button color="danger" onClick={runDirectDeliveryAlgorithm} disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Run Algorithm'}
              </Button>
            </Col>
          </Row>

          {errorMsg && (
            <Alert color="danger" className="mt-2">
              {errorMsg}
            </Alert>
          )}

          <Card>
            <CardBody>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <span className="ms-2">Loading logs...</span>
                </div>
              ) : directDeliveryLogs.length === 0 ? (
                <Alert color="info">No logs found.</Alert>
              ) : (
                <UncontrolledAccordion stayOpen>
                  {directDeliveryLogs.map((log) => {
                    return (
                      <AccordionItem key={log.id}>
                        <AccordionHeader targetId={String(log.id)}>
                          <div className="d-flex align-items-center w-100">
                            <div className="flex-grow-1">
                              <strong>Direct Delivery Algorithm #{log.id}</strong>
                              <div className="text-muted small">
                                Algorithm run at: {formatDate(log.createdAt)} at {formatTimestamp(log.createdAt)}
                              </div>
                            </div>
                            <Button 
                              color="danger" 
                              size="sm"
                              className="mx-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeDirectDeliveryLog(log.id);
                              }}
                              disabled={loading}
                            >
                              Remove
                            </Button>
                          </div>
                        </AccordionHeader>
                        <AccordionBody accordionId={String(log.id)}>
                          {/* Log Messages */}
                          <h5 className="mb-3">Algorithm Execution Logs</h5>
                          <div className="timeline-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {log.logs && log.logs.map((logEntry, index) => (
                              <div key={index} className="d-flex mb-2">
                                <div className="flex-grow-1 border rounded p-2 text-break">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="w-100">
                                      <small className="text-nowrap">{formatTimestamp(logEntry.timestamp)}</small> ➥ <small className="ms-1 text-black fw-bold text-wrap">{logEntry.message}</small>
                                      {/* <Badge className="text-dark ms-1 text-wrap" color="light" pill>{logEntry.message}</Badge> */}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="text-muted small mt-3">
                            Log created at: {new Date(log.createdAt).toLocaleString()}
                          </div>
                        </AccordionBody>
                      </AccordionItem>
                    );
                  })}
                </UncontrolledAccordion>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default DirectDeliveryLogs;